import classNames from 'classnames';

import Card from 'components/_shared/Card';
import { ManageDebit } from 'assets/index';
import EmptyMemberList from 'assets/EmptyMemberList.png';

import { createStyles, makeStyles } from '@mui/styles';

const useStyles = makeStyles(() =>
    createStyles({
        actionCard: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            height: '320px',
            width: '100%',
            maxWidth: '320px',
            textAlign: 'center'
        },
        activeCard: {
            cursor: 'pointer',
            '&:active': {
                transform: 'scale(0.98)'
            },
            '&:focus-visible': {
                outline: '2px solid #7378E8',
                outlineOffset: '4px'
            }
        },
        assignTitle: {
            color: '#1F204E',
            fontWeight: 700,
            fontSize: '20px'
        },
        checkCircle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#7FE8B2',
            borderRadius: '35px',
            color: '#FFFFFF',
            height: '64px',
            width: '64px'
        },
        debitCard: {
            height: '120px',
            width: 'auto'
        },
        memberCard: {
            height: '140px',
            width: 'auto'
        },
        overlay: {
            position: 'absolute',
            top: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#00000099',
            borderRadius: '16px',
            height: '100%',
            width: '100%',
        },
        overlayContent: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#7FE8B2',
            fontWeight: 800,
            fontSize: '24px',
            padding: '0px 32px',
            textAlign: 'center',
            gap: '16px',
            lineHeight: '1.3',
        }
    })
);

type CardType = {
    [key: string]: {
        className: string;
        image: string;
        subtitle: string;
        title: string;
    };
};

type OptionalActionCardProps = {
    type: string;
    memberName: string;
    handleCardClick?: () => void;
    completed: boolean;
    completedText: string;
};

const OptionalActionCard: React.FC<OptionalActionCardProps> = ({
    type,
    memberName,
    handleCardClick,
    completed,
    completedText
}) => {
    const classes = useStyles();

    const cardClass = classNames({
        [classes.actionCard]: true,
        [classes.activeCard]: handleCardClick !== undefined && !completed
    });

    const cardType: CardType = {
        debit: {
            className: classes.debitCard,
            image: ManageDebit,
            subtitle: `${memberName} can spend funds flexibly and immediately`,
            title: 'Assign Debit Card'
        },
        action_steps: {
            className: classes.memberCard,
            image: EmptyMemberList,
            subtitle: `Start creating actions to help ${memberName} reach their goals`,
            title: 'Create Action Step'
        }
    };

    const { className, image, subtitle, title } = cardType[type];

    const getAriaLabel = () => {
        if (completed) {
            return `${title} - ${completedText}`;
        }
        return title;
    };

    const isClickable = handleCardClick && !completed;

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (isClickable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            handleCardClick?.();
        }
    };

    return (
        <Card
            className={cardClass}
            onClick={isClickable ? handleCardClick : () => {}}
            {...({
                tabIndex: isClickable ? 0 : -1,
                role: 'button',
                onKeyDown: handleKeyDown,
                'aria-label': getAriaLabel()
            } as any)}
        >
            {completed && (
                <div className={classes.overlay}>
                    <div className={classes.overlayContent}>
                    <span className={classes.checkCircle}>
                        <i className="fas fa-check" />
                    </span>
                    {completedText}
                    </div>
                </div>
            )}
            <span className={classes.assignTitle}>{title}</span>
            <img src={image} alt="manage-action" className={className} />
            <span style={{ fontSize: '16px' }}>{subtitle}</span>
        </Card>
    );
};

export default OptionalActionCard;
